/**
 * The arguments step of the tool editor: the inputs the tool takes, filled in on the left, beside its
 * signature and what each input is.
 */

import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import { humanize } from '@/modules/simulator/ui/text';
import { ToolArguments } from './tool-arguments';
import { ToolDetails } from './tool-details';
import { schemaFields } from './tool-schema';

interface ToolCallFormProps {
	/** Null when what the tool takes is not known. */
	definition: ToolDefinitionAction | null;
	/** Integrations that have the tool with different inputs, when that is why the definition is null. */
	sharedBy: string[];
	args: Record<string, unknown>;
	onChange: (args: Record<string, unknown>) => void;
}

const unknownTool = (sharedBy: string[]): string =>
	sharedBy.length > 0
		? `${sharedBy.map(humanize).join(' and ')} each have this tool with different inputs, and the call does not say which one ran it. Its arguments can still be edited as they are.`
		: 'The account no longer has this tool, so what it takes is not known. Its arguments can still be edited as they are.';

export const ToolCallForm = ({ definition, sharedBy, args, onChange }: ToolCallFormProps) => (
	<div className='grid min-h-0 flex-1 overflow-y-auto border-t border-gray-500 @2xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] @2xl:overflow-hidden'>
		<div className='flex min-h-0 flex-col gap-3 border-b border-gray-500 p-4 @2xl:overflow-y-auto @2xl:border-r @2xl:border-b-0'>
			<p className='text-xs leading-4 font-medium text-text-heading'>Arguments</p>
			<ToolArguments args={args} fields={definition ? schemaFields(definition.inputSchema) : null} onChange={onChange} />
		</div>
		<div className='flex min-h-0 flex-col'>
			{definition ? (
				<ToolDetails tool={definition} />
			) : (
				<p className='p-4 text-xs leading-4 text-gray-800'>{unknownTool(sharedBy)}</p>
			)}
		</div>
	</div>
);
